'use client'

import { useEffect } from 'react'
import type { GtmAdditionalScriptType } from '@/lib/types/global-content'

type InjectTarget = 'head' | 'body-start' | 'body-end'

type Props = {
  scripts: GtmAdditionalScriptType[]
  target: InjectTarget
}

/**
 * Injects CMS HTML `<script>` snippets (src or inline) into head/body.
 * Recreates script nodes so the browser executes them.
 */
export function CmsAdditionalScripts({ scripts, target }: Props) {
  useEffect(() => {
    const parent =
      target === 'head'
        ? document.head
        : document.body
    if (!parent) return

    const created: HTMLElement[] = []

    for (const item of scripts) {
      const html = item.script?.trim()
      if (!html) continue

      const holder = document.createElement('div')
      holder.innerHTML = html

      holder.querySelectorAll('script').forEach((oldScript) => {
        const script = document.createElement('script')
        Array.from(oldScript.attributes).forEach((attr) => {
          script.setAttribute(attr.name, attr.value)
        })
        if (oldScript.textContent) {
          script.text = oldScript.textContent
        }
        script.dataset.cmsScriptId = String(item.id)
        if (item.scriptName) {
          script.dataset.cmsScriptName = item.scriptName
        }

        if (target === 'body-start' && parent.firstChild) {
          parent.insertBefore(script, parent.firstChild)
        } else {
          parent.appendChild(script)
        }
        created.push(script)
      })
    }

    return () => {
      created.forEach((node) => node.remove())
    }
  }, [scripts, target])

  return null
}
